import {
	IconCalendar,
	IconClipboardText,
	IconCloud,
	IconKey,
	IconLock,
	IconNumber123,
	IconRocket,
	IconShieldCheck,
	IconToggleRight,
	IconUser,
} from "@tabler/icons-react";
import { Switch } from "antd";
import { format } from "date-fns";
import { useState } from "react";
import type {
	AutoApprovalThreshold,
	EnvironmentClassification,
	EnvironmentStatus,
} from "@/common/artemis-sdk/graphql";
import {
	type Environment,
	EnvironmentStatus as EnvStatus,
} from "@/common/model";
import { IdField } from "../base";
import {
	BaseDetails,
	EditableClickable,
	EditableText,
	InlineSelect,
	toOptions,
} from "../base-details";
import { CustomDrawer } from "../layout/workspace/custom-drawer";
import { OwnerField } from "../users/owner-field";
import { AutomationPolicyDrawer } from "./automation-policy-drawer";
import {
	EnvironmentClassificationLabelMap,
	EnvironmentProviderLabelMap,
	EnvironmentStatusBadge,
	EnvironmentStatusLabelMap,
} from "./environment-badges";
import {
	PROVIDER_METADATA_FIELDS,
	THRESHOLD_OPTIONS,
} from "./environment-base";

interface EnvironmentDetailsProps {
	environment: NonNullable<Environment>;
	onSaveName?: (value: string) => void;
	onSaveClassification?: (value: EnvironmentClassification) => void;
	onSaveAutoApproveUpTo?: (value: AutoApprovalThreshold) => void;
	onSaveStatus?: (value: EnvironmentStatus) => void;
	onSaveAllowAgentsToBeDeployed?: (value: boolean) => void;
	onSaveAllowAgentsToAccess?: (value: boolean) => void;
	/** Hands ownership to another workspace member. When omitted, the owner is read-only. */
	onSaveOwner?: (userId: string) => void;
}

/** Read-only or editable detail view for a single environment. Fields without an onSave are read-only. */
export function EnvironmentDetails({
	environment,
	onSaveName,
	onSaveClassification,
	onSaveAutoApproveUpTo,
	onSaveStatus,
	onSaveAllowAgentsToBeDeployed,
	onSaveAllowAgentsToAccess,
	onSaveOwner,
}: EnvironmentDetailsProps) {
	const isDisabled = environment.status === EnvStatus.DISABLED;
	const [policyOpen, setPolicyOpen] = useState(false);

	// The agent switches are backed by role ARNs stored in metadata: without an operations/max
	// role agents can't access the environment, and without the deployment role they can't be
	// deployed — so the corresponding switch is greyed out until the role exists. (These are
	// AWS-specific fields; providers that define none keep the switches disabled.)
	const configuredKeys = new Set(
		(environment.metadata ?? [])
			.filter((m) => m.value?.trim())
			.map((m) => m.key),
	);
	const providerFields = PROVIDER_METADATA_FIELDS[environment.provider] ?? [];
	const hasAccessRole = providerFields.some(
		(f) => f.group === "agentAccess" && !f.secret && configuredKeys.has(f.key),
	);
	const hasDeploymentRole = providerFields.some(
		(f) => f.deploymentIndicator && configuredKeys.has(f.key),
	);
	return (
		<>
			<BaseDetails
				item={environment}
				fields={[
					{
						key: "id",
						tooltip: "Id",
						icon: IconNumber123,
						render: (id) => <IdField id={id} showCopyButton />,
					},
					{
						key: "name",
						tooltip: "Name",
						icon: IconClipboardText,
						render: (name) => (
							<EditableText onChange={onSaveName}>
								{name as string}
							</EditableText>
						),
					},
					{
						key: "status",
						tooltip: "Status",
						icon: IconToggleRight,
						render: (status) => (
							<InlineSelect
								value={status}
								options={Object.entries(EnvironmentStatusLabelMap)
									.filter(([value]) => value !== "FAILED")
									.map(([value, label]) => ({
										value: value as EnvironmentStatus,
										label,
									}))}
								onChange={onSaveStatus}
							>
								<EnvironmentStatusBadge status={status} />
							</InlineSelect>
						),
					},
					{
						key: "provider",
						tooltip: "Provider",
						icon: IconCloud,
						// Attributes render as plain text; tags are reserved for state (Status).
						render: (provider) => EnvironmentProviderLabelMap[provider],
					},
					{
						key: "classification",
						tooltip: "Classification",
						icon: IconShieldCheck,
						render: (classification) => (
							<InlineSelect
								value={classification}
								options={toOptions(EnvironmentClassificationLabelMap)}
								onChange={onSaveClassification}
							>
								{EnvironmentClassificationLabelMap[classification]}
							</InlineSelect>
						),
					},
					{
						key: "autoApproveUpTo",
						tooltip: "Automation policy",
						icon: IconLock,
						// Opens a drawer rather than an inline select: the policy only makes sense next to
						// the ladder of what it auto-approves, and a details row has no room for it.
						render: (threshold) => {
							const label = THRESHOLD_OPTIONS.find(
								(o) => o.value === threshold,
							)?.label;
							return onSaveAutoApproveUpTo ? (
								<EditableClickable onClick={() => setPolicyOpen(true)}>
									{label}
								</EditableClickable>
							) : (
								label
							);
						},
					},
					{
						// User-controlled flag: show the stored value even when the environment is
						// disabled (the switch greys out instead of flipping off).
						key: "allowAgentsToBeDeployed",
						tooltip: "Can host agents",
						icon: IconRocket,
						render: (allow) => (
							<Switch
								size="small"
								checked={!!allow}
								onChange={onSaveAllowAgentsToBeDeployed}
								disabled={
									!onSaveAllowAgentsToBeDeployed ||
									isDisabled ||
									!hasDeploymentRole
								}
							/>
						),
					},
					{
						key: "allowAgentsToAccess",
						tooltip: "Agents can access",
						icon: IconKey,
						render: (allow) => (
							<Switch
								size="small"
								checked={!!allow}
								onChange={onSaveAllowAgentsToAccess}
								disabled={
									!onSaveAllowAgentsToAccess || isDisabled || !hasAccessRole
								}
							/>
						),
					},
					{
						key: "ownerId",
						tooltip: "Owner",
						icon: IconUser,
						render: (id) => (
							<OwnerField
								ownerId={id}
								workspaceId={environment.workspaceId}
								onSave={onSaveOwner}
							/>
						),
					},
					{
						key: "createdAt",
						tooltip: "Created",
						icon: IconCalendar,
						render: (createdAt) =>
							format(new Date(createdAt as string), "MM/dd/yyyy h:mm a"),
					},
				]}
			/>
			{onSaveAutoApproveUpTo && (
				<AutomationPolicyDrawer
					open={policyOpen}
					onClose={() => setPolicyOpen(false)}
					current={environment.autoApproveUpTo as AutoApprovalThreshold}
					onSave={onSaveAutoApproveUpTo}
				/>
			)}
		</>
	);
}

export function EnvironmentDetailsDrawer({
	environment,
	open,
	onClose,
	onSaveName,
	onSaveClassification,
	onSaveAutoApproveUpTo,
	onSaveStatus,
	onSaveAllowAgentsToBeDeployed,
	onSaveAllowAgentsToAccess,
}: {
	environment?: NonNullable<Environment>;
	open: boolean;
	onClose: () => void;
	onSaveName?: (value: string) => void;
	onSaveClassification?: (value: EnvironmentClassification) => void;
	onSaveAutoApproveUpTo?: (value: AutoApprovalThreshold) => void;
	onSaveStatus?: (value: EnvironmentStatus) => void;
	onSaveAllowAgentsToBeDeployed?: (value: boolean) => void;
	onSaveAllowAgentsToAccess?: (value: boolean) => void;
}) {
	if (!environment) return null;
	return (
		<CustomDrawer title={environment.name} open={open} onClose={onClose}>
			<EnvironmentDetails
				environment={environment}
				onSaveName={onSaveName}
				onSaveClassification={onSaveClassification}
				onSaveAutoApproveUpTo={onSaveAutoApproveUpTo}
				onSaveStatus={onSaveStatus}
				onSaveAllowAgentsToBeDeployed={onSaveAllowAgentsToBeDeployed}
				onSaveAllowAgentsToAccess={onSaveAllowAgentsToAccess}
			/>
		</CustomDrawer>
	);
}
